import React from 'react';
import { useTranslation } from 'react-i18next';

import { CopyButton } from '@/components/CopyButton';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Typography } from '@/components/Typography';

export type JsonValueRenderer = (
  _key: string,
  _value: unknown,
  _path: string[],
) => React.ReactNode | null;

interface JsonViewerSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  data: unknown;
  renderValue?: JsonValueRenderer;
}

export const renderJsonWithCustomValues = (
  data: unknown,
  renderValue?: JsonValueRenderer,
  path: string[] = [],
  indent: number = 0,
): React.ReactNode => {
  if (data === null) {
    return <span className='text-warning'>null</span>;
  }

  if (typeof data === 'boolean') {
    return <span className='text-warning'>{data.toString()}</span>;
  }

  if (typeof data === 'number') {
    return <span className='text-info'>{data}</span>;
  }

  if (typeof data === 'string') {
    return <span className='text-success'>"{data}"</span>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return '[]';
    }

    return (
      <>
        {'[\n'}
        {data.map((item, index) => {
          const itemPath = [...path, index.toString()];
          return (
            <React.Fragment key={index}>
              {'  '.repeat(indent + 1)}
              {renderJsonWithCustomValues(
                item,
                renderValue,
                itemPath,
                indent + 1,
              )}
              {index < data.length - 1 ? ',\n' : '\n'}
            </React.Fragment>
          );
        })}
        {'  '.repeat(indent)}
        {']'}
      </>
    );
  }

  if (typeof data === 'object') {
    const entries = Object.entries(data);
    if (entries.length === 0) {
      return '{}';
    }

    return (
      <>
        {'{\n'}
        {entries.map(([key, value], index) => {
          const valuePath = [...path, key];
          const customRendered = renderValue?.(key, value, valuePath);

          return (
            <React.Fragment key={key}>
              {'  '.repeat(indent + 1)}
              <span className='text-primary'>"{key}"</span>
              {': '}
              {customRendered !== null && customRendered !== undefined
                ? customRendered
                : renderJsonWithCustomValues(
                    value,
                    renderValue,
                    valuePath,
                    indent + 1,
                  )}
              {index < entries.length - 1 ? ',\n' : '\n'}
            </React.Fragment>
          );
        })}
        {'  '.repeat(indent)}
        {'}'}
      </>
    );
  }

  return String(data);
};

export const JsonViewerSheet = ({
  open,
  onClose,
  title,
  data,
  renderValue,
}: JsonViewerSheetProps) => {
  const { t } = useTranslation();

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent className='flex w-full flex-col gap-0 p-0 sm:max-w-2xl'>
        <SheetHeader className='border-b px-6 py-4'>
          <div className='flex items-center gap-2 pr-8'>
            <SheetTitle>{title}</SheetTitle>
            <CopyButton
              value={JSON.stringify(data, null, 2)}
              label={t('common.copy')}
            />
          </div>
          <SheetDescription className='sr-only'>{title}</SheetDescription>
        </SheetHeader>
        <Typography
          variant='monospaced'
          as='pre'
          color='foreground'
          className='flex-1 overflow-auto bg-muted/40 p-6 text-xs leading-5 whitespace-pre-wrap break-words'
        >
          {renderJsonWithCustomValues(data, renderValue)}
        </Typography>
      </SheetContent>
    </Sheet>
  );
};
